import type { CategoryId } from '@timelog/pm-shared';
import { contingencySeedShare } from '@timelog/pm-shared';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import {
  flowReducer,
  skippedCategories,
  startFlow,
  stepCategory,
  withLiveVisibility,
} from './entryFlow';
import type { FlowMode, FlowState, FlowStep } from './entryFlow';

import { captureEvent } from '@/analytics/posthogClient';
import { useTimeLogRates } from '@/api/timelogHooks';
import { useFinancials } from '@/components/financials/editor';

/**
 * Wires the pure flow machine to the contract store: every transition's
 * document lands as one APPLY_OP commit (one revision → autosave coalesces,
 * BR-21). Cards consult this context to render their input as the active
 * step and to route commits into the walk instead of the ordinary reducer.
 */
export type GuidedEntryValue = {
  /** Null = no walk running. */
  flow: FlowState | null;
  start: (mode: FlowMode) => void;
  /** value null = unparsable input (stay on the step). */
  commit: (value: number | null) => void;
  skip: () => void;
  exit: () => void;
  /** The step currently editing inside the given card, if any. */
  activeStepFor: (categoryId: CategoryId) => FlowStep | null;
};

const GuidedEntryContext = createContext<GuidedEntryValue | undefined>(undefined);

export const GuidedEntryProvider = ({ children }: { children: ReactNode }) => {
  const { state, dispatch, blendedCostRate, budgetModel } = useFinancials();
  /*
    An empty contract opens IN the walk rather than behind a button: the page
    has exactly one thing to ask for first, and "Start guided entry" was a click
    between the user and a field whose placeholder already says what to type.

    Seeded here rather than from an effect, so it happens once when the provider
    mounts and never fights the user afterwards. Escape exits the walk and falls
    through to the ordinary click-to-edit total — which an effect watching
    "contract is empty" would have undone on the next render, trapping them.
    `ContractPanel` keys this provider by contract, so selecting a different
    empty contract mounts a fresh walk.
  */
  const [flow, setFlow] = useState<FlowState | null>(() =>
    state.document.revenue === 0
      ? startFlow(
          state.type === 'tm' ? 'tm' : 'fixed-amount',
          state.document,
          skippedCategories(budgetModel.config, state.document),
        )
      : null,
  );
  // Fetched up front (cached 5 min) so the copy is there when the T&M hours
  // are committed; the hook is inert while TIMELOG_MODE=off.
  const defaultRates = useTimeLogRates(true);

  const context = useMemo(
    () => ({
      blendedCostRate,
      contingencyShare: contingencySeedShare(budgetModel.config),
      ...(defaultRates.data ? { defaultRates: defaultRates.data.items } : {}),
    }),
    [blendedCostRate, budgetModel.config, defaultRates.data],
  );

  const start = useCallback(
    (mode: FlowMode) => {
      setFlow(
        startFlow(mode, state.document, skippedCategories(budgetModel.config, state.document)),
      );
    },
    [state.document, budgetModel.config],
  );

  const advance = useCallback(
    (event: { type: 'COMMIT'; value: number } | { type: 'SKIP' } | { type: 'EXIT' }) => {
      setFlow((current) => {
        if (!current) return current;
        const result = flowReducer(
          withLiveVisibility(current, state.document, budgetModel.config),
          event,
          context,
        );
        if (result.document) {
          const document = result.document;
          dispatch({ type: 'APPLY_OP', op: () => document });
        }
        if (result.state === null && result.document && event.type !== 'EXIT') {
          captureEvent('guided_entry_completed', { mode: current.mode });
        }
        return result.state;
      });
    },
    [context, dispatch, state.document, budgetModel.config],
  );

  const commit = useCallback(
    (value: number | null) => {
      if (value === null) return; // unparsable — keep the step
      advance({ type: 'COMMIT', value });
    },
    [advance],
  );

  const skip = useCallback(() => advance({ type: 'SKIP' }), [advance]);
  const exit = useCallback(() => advance({ type: 'EXIT' }), [advance]);

  const activeStepFor = useCallback(
    (categoryId: CategoryId) => (flow && stepCategory(flow.step) === categoryId ? flow.step : null),
    [flow],
  );

  const value = useMemo<GuidedEntryValue>(
    () => ({ flow, start, commit, skip, exit, activeStepFor }),
    [flow, start, commit, skip, exit, activeStepFor],
  );

  return <GuidedEntryContext.Provider value={value}>{children}</GuidedEntryContext.Provider>;
};

export const useGuidedEntry = (): GuidedEntryValue => {
  const context = useContext(GuidedEntryContext);
  if (!context) {
    throw new Error('useGuidedEntry must be used inside a GuidedEntryProvider');
  }
  return context;
};
